import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import { getPostBySlug, BLOG_POSTS } from '../data/blogPosts';

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);

  const currentIndex = BLOG_POSTS.findIndex(p => p.slug === slug);
  const nextPost = BLOG_POSTS[currentIndex + 1] || null;

  if (!post) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center text-zinc-950 px-6">
        <div className="font-bebas text-6xl mb-4">Post Not Found</div>
        <p className="text-zinc-500 mb-8 text-sm">This content requires a DLC pack. Or it simply doesn't exist.</p>
        <button onClick={() => navigate('/blog')} className="flex items-center gap-2 bg-zinc-950 text-white font-bold uppercase tracking-widest text-xs px-6 py-3 border-2 border-zinc-950">
          <ArrowLeft size={11} strokeWidth={3} /> Back to Blog
        </button>
      </div>
    );
  }

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.description,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": { "@type": "Organization", "name": "CalcByEA", "url": "https://calculatorbyea.com" },
    "publisher": { "@type": "Organization", "name": "CalcByEA", "url": "https://calculatorbyea.com" },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://calculatorbyea.com/blog/${post.slug}` },
  };

  const faqSchema = post.faqItems ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": post.faqItems.map(({ question, answer }) => ({
      "@type": "Question",
      "name": question,
      "acceptedAnswer": { "@type": "Answer", "text": answer },
    })),
  } : null;

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950">
      <Helmet>
        <title>{`${post.title} | CalcByEA™`}</title>
        <meta name="description" content={post.description} />
        <link rel="canonical" href={`https://calculatorbyea.com/blog/${post.slug}`} />
        <meta property="og:url" content={`https://calculatorbyea.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="article:published_time" content={post.date} />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      <Navbar />

      <div className="pt-14">
        {/* Article header */}
        <div className="border-b-2 border-zinc-950 bg-white">
          <div className="max-w-3xl mx-auto px-6 pt-16 pb-12">
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-950 transition-colors mb-8"
              data-testid="back-to-blog-btn"
            >
              <ArrowLeft size={11} strokeWidth={3} /> Blog
            </button>
            <div className="flex items-center gap-3 mb-5">
              <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-400 border border-zinc-200 px-2 py-0.5">{post.category}</span>
              <span className="text-[9px] text-zinc-400 uppercase tracking-widest">{post.date}</span>
              <span className="text-[9px] text-zinc-400 uppercase tracking-widest">{post.readTime}</span>
            </div>
            <h1 className="font-bebas text-5xl sm:text-6xl text-zinc-950 leading-tight mb-6">{post.title}</h1>
            <p className="text-zinc-500 text-lg leading-relaxed border-l-4 border-zinc-950 pl-5">{post.excerpt}</p>
          </div>
        </div>

        {/* Article body */}
        <div className="max-w-3xl mx-auto px-6 py-14">
          <div
            className="prose-article"
            data-testid="blog-post-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>

        {/* Next post */}
        {nextPost && (
          <div className="border-t-2 border-zinc-950 bg-white">
            <div className="max-w-3xl mx-auto px-6 py-10 flex items-center justify-between gap-6">
              <div>
                <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-1">Next Article</div>
                <div className="font-bebas text-2xl text-zinc-950 leading-tight">{nextPost.title}</div>
              </div>
              <button
                onClick={() => navigate(`/blog/${nextPost.slug}`)}
                className="shrink-0 flex items-center gap-2 bg-zinc-950 text-white font-bold uppercase tracking-widest text-xs px-5 py-3 border-2 border-zinc-950 shadow-[3px_3px_0px_0px_rgba(9,9,11,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(9,9,11,1)] transition-all duration-100"
                data-testid="next-post-btn"
              >
                Read <ArrowRight size={11} strokeWidth={3} />
              </button>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="border-t border-zinc-200 bg-zinc-50">
          <div className="max-w-3xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-zinc-950">The equals sign is $9.99.</p>
              <p className="text-xs text-zinc-400 mt-0.5">You read the theory. Try the practice.</p>
            </div>
            <a
              href="/calculator"
              className="flex items-center gap-2 border-2 border-zinc-950 text-zinc-950 font-bold uppercase tracking-widest text-xs px-5 py-3 shadow-[3px_3px_0px_0px_rgba(9,9,11,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(9,9,11,1)] transition-all duration-100 shrink-0"
            >
              Try the Calculator <ArrowRight size={11} strokeWidth={3} />
            </a>
          </div>
        </div>
      </div>

      <footer className="py-10 px-6 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[10px] font-bold text-zinc-300 uppercase tracking-widest">
            EA is a trademark of Electronic Arts. This site is a parody. &nbsp;·&nbsp;{' '}
            <a href="https://itorzo.com" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-500 transition-colors">An Itorzo product</a>
          </p>
          <div className="flex items-center gap-5">
            <a href="/blog" className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest hover:text-zinc-950 transition-colors">Blog</a>
            <a href="/privacy" className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest hover:text-zinc-950 transition-colors">Privacy</a>
            <a href="/contact" className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest hover:text-zinc-950 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
